import { useEffect, useState } from "react";
import { Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import type { UiTheme } from "../theme";
import { useReducedMotion } from "../useReducedMotion";
import { AddDayChip, AddRoundChip } from "./GridAddChips";
import type { GridGestureBundle } from "./useGridGestures";
import type { makeGridStyles } from "./gridStyles";

// PE-27/PE-29 — the grid's structural handles. It used to carry four controls:
// two ＋ and two armed − that named their target ("− W1", "− D3").
//
// ⭐ PE-29 (Antenore, smoke 28/08) — the two − are gone, and not just because
// they were "bruttissimi". They were the odd gesture out: this grid already
// teaches hold-to-act on a cell (place/remove the focused exercise, PE-22 F),
// so a week or a day is now removed by holding ITS OWN header, with the
// platform confirm. That puts the action on the thing acted upon instead of on
// a button that has to say out loud which one it means — and it is why the
// labels here can shrink to "D ＋" / "W ＋" without losing anything: the ＋ no
// longer has a − beside it to be told apart from.
// ⛔ The hold is invisible until tried, so the row STATES it. A gesture nobody
// is told about is a gesture nobody has.
//
// ⭐ PE-45 (Antenore, 30/08) — `W ＋` now answers to BOTH gestures, and the
// difference between them is the difference between the two things an author
// means by "one more week": tap adds an EMPTY week, hold adds a week that
// REPEATS the one on screen. The clone stopped being a bar of its own when
// PE-39 deleted the classic shell; it comes back as the hold on the control
// that already means "one more".
//
// ⛔ And that is why the hint ROTATES instead of growing. Four holds now live
// in this grid and the row fits one sentence: naming one of them teaches one,
// naming all four at once is a paragraph. The visible line takes turns; the
// screen reader gets the whole list in one label, because a label that changes
// under the reader is a label it never finishes (useCyclingHint below).

/** Floor, ms. Short lines still get long enough to be noticed and read. */
const MIN_DWELL_MS = 4000;
/**
 * Per word, ms.
 *
 * ⚠️ AN ASSUMPTION, stated so it can be corrected rather than inherited: ~100
 * words/minute, about half a typical sighted rate, because the reader this app
 * is built for reads with reduced vision and this line renders at `xs`. It is
 * not measured on him.
 *
 * ⭐ What it replaces matters more than its exact value: a flat interval gives
 * the German line ("Eine Zelle gedrückt halten, um die Übung dort einzusetzen",
 * nine words) the same window as a four-word English one, so the sentence can
 * change mid-read in exactly one locale. Deriving the dwell from the STRING
 * makes translation length a fact the timing already knows, instead of a defect
 * discovered per locale [[feedback_derive_thresholds_from_data_not_constants]].
 */
const MS_PER_WORD = 600;

export const dwellMsFor = (text: string): number =>
  Math.max(MIN_DWELL_MS, text.trim().split(/\s+/).length * MS_PER_WORD);

// ⛔ Local, not a primitive in `primitives/`: it has exactly one caller, and a
// shared abstraction with one callsite is a guess about the second. It moves
// out of this file the day something else needs it, not before.
//
// ⛔ Reduced motion stops the cycle rather than speeding it up: text that swaps
// under the eye is the kind of movement that setting asks us not to make. It
// falls back to the FIRST item and not to silence, because losing the
// instruction is not an accessible outcome either — the same call `UnseenCue`
// makes with its static mark. ⚠️ That setting is also the only way to stop the
// rotation; a longer text, or one a reader has to act on, would want a control.
function useCyclingHint(items: readonly string[]): string | null {
  const [idx, setIdx] = useState(0);
  const reducedMotion = useReducedMotion();
  const n = items.length;
  // Clamped on read, not repaired in an effect: the list shrinks when a gesture
  // stops being available (the last week can no longer be removed), and the
  // render that would show the hole is the one an effect arrives too late for.
  const current = n === 0 ? null : (items[idx % n] ?? items[0] ?? null);

  useEffect(() => {
    // Nothing to rotate through: one item is already permanent, zero has no
    // line at all. Not starting the clock matters — a row that ticks forever to
    // say the same thing is a wakeup for nothing, and a timer left running is
    // what turns a test that forgets to unmount into a dead jest worker.
    if (reducedMotion || n < 2 || current === null) {
      setIdx(0);
      return;
    }
    // ⛔ A timeout per item, not one interval for all of them: the dwell is a
    // property of the sentence on screen, so it is re-decided every time the
    // sentence changes.
    const id = setTimeout(() => setIdx((i) => (i + 1) % n), dwellMsFor(current));
    return () => clearTimeout(id);
  }, [reducedMotion, n, current]);

  return current;
}

export function GridStructureRow({
  theme,
  styles,
  onAddDay,
  onAddWeek,
  weekCloneGesture,
  canRemoveWeek,
  canRemoveDay,
  canPlace,
}: {
  theme: UiTheme;
  styles: ReturnType<typeof makeGridStyles>;
  onAddDay?: () => void;
  onAddWeek?: () => void;
  /** PE-45 — the hold on `W ＋`, from useGridGestures. Empty bundle ⇒ no hold. */
  weekCloneGesture?: GridGestureBundle;
  /** A week can be held to remove it ⇒ the hint says so. */
  canRemoveWeek: boolean;
  /** A day can be held to remove it ⇒ the hint says so. */
  canRemoveDay: boolean;
  /** An exercise is focused, so a cell can be held to place it ⇒ the hint says
   *  so. ⛔ Until PE-45 this gesture was taught NOWHERE: the row named the two
   *  removals and stayed silent about the one hold an author uses constantly. */
  canPlace?: boolean;
}) {
  const { t } = useTranslation();
  const canCloneWeek = weekCloneGesture?.onLongPress != null;

  // ⛔ The list names only what is actually holdable RIGHT NOW. On a one-week
  // program the week cannot be removed, and telling someone to hold it would be
  // a lie they find out by holding.
  //
  // ⭐ THE ORDER IS A DECISION, not the order they were written in. Someone who
  // reads this row once and then taps sees the FIRST line and no other, so the
  // list leads with the gesture nobody could already know — the clone, which
  // did not exist before, and the cell hold, which was never taught anywhere.
  // The two removals come last: they shipped with a hint of their own in PE-29
  // and are the ones an author has already met.
  const hints = [
    canCloneWeek
      ? t("composer.holdAddWeekToCloneHint", {
          defaultValue: "Hold W ＋ to repeat this week",
        })
      : null,
    canPlace
      ? t("composer.holdCellToPlaceHint", {
          defaultValue: "Hold a cell to place the exercise there",
        })
      : null,
    canRemoveDay
      ? t("composer.holdDayToRemoveHint", {
          defaultValue: "Hold a day to remove it",
        })
      : null,
    canRemoveWeek
      ? t("composer.holdWeekToRemoveHint", {
          defaultValue: "Hold a week to remove it",
        })
      : null,
  ].filter((h): h is string => h !== null);

  const hint = useCyclingHint(hints);

  return (
    <View style={styles.gridStructureRow}>
      {onAddDay ? <AddDayChip theme={theme} onPress={onAddDay} /> : null}
      {onAddWeek ? <AddRoundChip theme={theme} onPress={onAddWeek} gesture={weekCloneGesture} /> : null}
      {hint ? (
        <Text
          style={styles.gridStructureHint}
          // Every gesture at once, in one utterance, instead of the rotation.
          accessibilityLabel={hints.join(". ")}
        >
          {hint}
        </Text>
      ) : null}
    </View>
  );
}
